import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './IvMeasurementLineChart.css';
import '../../App.css';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CSVLink } from 'react-csv';
import { ko } from 'date-fns/locale';

import config from '../../config';
import { formatPower } from '../../public/formatPower';
import {
  TEN_MINUTE_LABELS,
  HOURLY_LABELS,
  findClosestTimeData,
  formatDateKST,
} from '../statistics/report/utils/hourlyChartUtils';

/** API 구간 증분(Wh) → CSV kWh 문자열 */
const formatIntervalAddPowerKwh = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return formatPower(value, 'Add').value;
};

/**
 * 툴팁/CSV용 구간 발전량 선택
 * - 10분 보기: Add_power_10m (10분 증분, baseline=당일 DB 첫 누적값)
 * - 1시간 보기: Add_power_1h (해당 시간대 10분 증분 6개 합)
 * API daily_by_seq: 일일 발전량과 동일한 seq별 합계(Wh) — Σ10분과 대조용
 */
const getIntervalAddPower = (item, viewHourly) => {
  if (!item) return null;
  return viewHourly ? item.Add_power_1h : item.Add_power_10m;
};

const IvMeasurementLineChart = ({ SelInverter }) => {
  const [chartData, setChartData] = useState(new Array(TEN_MINUTE_LABELS.length).fill(0));
  const [tooltipData, setTooltipData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewHourly, setViewHourly] = useState(false);

  const hourlyIndices = useMemo(
    () => HOURLY_LABELS.map((label) => TEN_MINUTE_LABELS.indexOf(label)),
    []
  );

  // 1시간 보기: 별도 API 없이 10분 데이터 중 정각 슬롯만 필터 (발전량은 Add_power_1h 사용)
  const { displayLabels, displayChartData, displayTooltipData } = useMemo(() => {
    if (!viewHourly) {
      return {
        displayLabels: TEN_MINUTE_LABELS,
        displayChartData: chartData,
        displayTooltipData: tooltipData,
      };
    }

    return {
      displayLabels: HOURLY_LABELS,
      displayChartData: hourlyIndices.map((index) => chartData[index]),
      displayTooltipData: hourlyIndices.map((index) => tooltipData[index]),
    };
  }, [viewHourly, chartData, tooltipData, hourlyIndices]);

  const fetchData = useCallback(async () => {
    try {
      const selectedDateInKST = formatDateKST(selectedDate);
      const response = await fetch(`${config.backendUrl}/get_hourly_generation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          date: selectedDateInKST,
          seq: SelInverter,
        }),
      });

      const result = await response.json();
      if (result.status !== 'success' || !Array.isArray(result.data)) {
        setChartData(new Array(TEN_MINUTE_LABELS.length).fill(0));
        setTooltipData([]);
        return;
      }

      const filtered = result.data.filter((item) => item.seq === SelInverter);
      const chartDataByTime = [];
      const tooltipDataByTime = [];

      TEN_MINUTE_LABELS.forEach((label) => {
        const { value, item } = findClosestTimeData(filtered, label);
        chartDataByTime.push(value);
        tooltipDataByTime.push(item);
      });

      setChartData(chartDataByTime);
      setTooltipData(tooltipDataByTime);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [SelInverter, selectedDate]);

  useEffect(() => {
    if (SelInverter) {
      fetchData();
      const interval = setInterval(fetchData, 10 * 60 * 1000);

      return () => clearInterval(interval);
    }
  }, [SelInverter, fetchData]);

  const intervalLabel = viewHourly ? '1시간 발전량' : '10분 발전량';

  const generateCSVData = () => {
    return displayLabels.map((label, index) => {
      const gridPower = displayChartData[index] === null
        || displayChartData[index] === undefined
        || displayChartData[index] === 0
        ? 'N/A'
        : (displayChartData[index] / 1000).toFixed(3);
      const tooltip = displayTooltipData[index];

      const formatValue = (value, divisor, decimals) => {
        if (!value || value === 'N/A' || value === null || value === undefined) return 'N/A';
        return (typeof value === 'number' ? value / divisor : parseFloat(value) / divisor).toFixed(decimals);
      };

      return {
        Time: label,
        GridPower: gridPower,
        AddPowerKwh: formatIntervalAddPowerKwh(getIntervalAddPower(tooltip, viewHourly)),
        created_at: tooltip?.created_at || 'N/A',
        PvTotalVolt: formatValue(tooltip?.PvTotalVolt, 10, 1),
        PvTotalCurrent: formatValue(tooltip?.PvTotalCurrent, 10, 1),
        PvPower: formatValue(tooltip?.PvPower, 1000, 3),
        Grid_RS_Volt: formatValue(tooltip?.Grid_RS_Volt, 10, 1),
        Grid_ST_Volt: formatValue(tooltip?.Grid_ST_Volt, 10, 1),
        Grid_TR_Volt: formatValue(tooltip?.Grid_TR_Volt, 10, 1),
      };
    });
  };

  const csvHeaders = [
    { label: '시간', key: 'Time' },
    { label: '인버터출력 (kW)', key: 'GridPower' },
    { label: `${intervalLabel} (kWh)`, key: 'AddPowerKwh' },
    { label: '측정시각', key: 'created_at' },
    { label: 'PV 총전압 (V)', key: 'PvTotalVolt' },
    { label: 'PV 총전류 (A)', key: 'PvTotalCurrent' },
    { label: 'PV 출력 (kW)', key: 'PvPower' },
    { label: '계통 RS전압 (V)', key: 'Grid_RS_Volt' },
    { label: '계통 ST전압 (V)', key: 'Grid_ST_Volt' },
    { label: '계통 TR전압 (V)', key: 'Grid_TR_Volt' },
  ];

  const data = {
    labels: displayLabels,
    datasets: [
      {
        data: displayChartData,
        backgroundColor: 'rgba(255, 159, 64, 0.6)',
        borderColor: 'rgba(255, 159, 64, 1)',
        borderWidth: 1,
        maxBarThickness: 20,
      },
    ],
  };

  const options = {
    scales: {
      y: {
        title: {
          display: true,
          text: '인버터출력 (kW)',
        },
        beginAtZero: true,
        ticks: {
          precision: 1,
          stepSize: (context) => {
            const maxVal = Math.max(...displayChartData.map((val) => val / 1000));
            return maxVal <= 1 ? 0.1 : Math.ceil(maxVal);
          },
          callback(value) {
            return (value / 1000).toFixed(1);
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label(tooltipItem) {
            const index = tooltipItem.dataIndex;
            const item = displayTooltipData[index];
            if (!item) return 'No Data';

            const intervalValue = getIntervalAddPower(item, viewHourly);
            const intervalPower = intervalValue == null
              ? { value: 'N/A', unit: '' }
              : formatPower(intervalValue, 'Add');

            return [
              `Grid Power: ${(item.Grid_Power / 1000).toFixed(3)} kW`,
              `${intervalLabel}: ${intervalPower.value}${intervalPower.unit ? ` ${intervalPower.unit}` : ''}`,
              `Time: ${item.created_at}`,
              `PvTotalVolt: ${(item.PvTotalVolt / 10).toFixed(1)} V`,
              `PvTotalCurrent: ${(item.PvTotalCurrent / 10).toFixed(1)} A`,
              `PvPower: ${(item.PvPower / 1000).toFixed(3)} kW`,
              `Grid_RS_Volt: ${(item.Grid_RS_Volt / 10).toFixed(1)} V`,
              `Grid_ST_Volt: ${(item.Grid_ST_Volt / 10).toFixed(1)} V`,
              `Grid_TR_Volt: ${(item.Grid_TR_Volt / 10).toFixed(1)} V`,
            ];
          },
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: 20,
    },
    backgroundColor: '#ffffff',
  };

  return (
    <div className="BarChart-Container">
      <div className="datepicker-container">
        <DatePicker
          selected={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          dateFormat="yyyy-MM-dd"
          maxDate={new Date()}
          className="styled-datepicker"
          locale={ko}
          customInput={<input className="datepicker-input" />}
        />

        <CSVLink
          data={generateCSVData()}
          headers={csvHeaders}
          filename={`iv_data_${formatDateKST(selectedDate)}${viewHourly ? '_hourly' : ''}.csv`}
          className="styled-button csv-download-button"
          style={{ backgroundColor: ' #1D6C41' }}
        >
          CSV 다운로드
        </CSVLink>

        <label className="hourly-view-checkbox">
          <input
            type="checkbox"
            checked={viewHourly}
            onChange={(e) => setViewHourly(e.target.checked)}
          />
          1시간 단위로 보기
        </label>
      </div>

      <div style={{ flex: 1, width: '1000px', height: '300px' }}>
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};

export default IvMeasurementLineChart;
